import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './auth';
import { LiveProvider } from './live';
import { ToastProvider } from './ui';
import { AcceptInvite, ResetPassword, SignIn, TwoFactorSetup } from './pages/Auth';
import { Account } from './pages/Account';
import { ProjectDetail, ProjectList } from './pages/Projects';
import { RoundViewer } from './pages/Designs';
import { ReviewApp } from './pages/Review';
import { ChangePage, SpecPage } from './pages/Spec';
import { QuestionsPage } from './pages/Questions';
import { MyTasks } from './pages/Tasks';
import { PipelinePage } from './pages/Pipeline';
import { ReportsPage } from './pages/Reports';
import { KitPage } from './pages/Kits';
import { BackupsPage } from './pages/Backups';
import { Page, Shell } from './pages/Shell';
import { ClientDetail, ClientList } from './pages/Clients';
import { People } from './pages/People';
import { AuditLog } from './pages/Audit';
import './styles.css';

const queryClient = new QueryClient({ defaultOptions: { queries: { refetchOnWindowFocus: true, staleTime: 15_000 } } });

function App() {
  const { me, loading, needs2fa } = useAuth();
  const location = useLocation();
  if (location.pathname === '/invite') return <AcceptInvite />;
  if (location.pathname === '/reset') return <ResetPassword />;
  if (loading) return <div className="auth-wrap"><span className="spin muted" /></div>;
  if (!me) return <SignIn />;
  if (needs2fa) return <TwoFactorSetup />;
  return (
    <LiveProvider enabled>
      <Routes>
        <Route element={<Shell />}>
          <Route index element={me.is_staff ? <Navigate to="/projects" replace /> : <ProjectList />} />
          <Route path="projects" element={me.is_staff ? <ProjectList /> : <Navigate to="/" replace />} />
          <Route path="projects/:id" element={<ProjectDetail />} />
          <Route path="projects/:id/designs/:n" element={<RoundViewer />} />
          <Route path="projects/:id/spec" element={me.is_staff ? <SpecPage /> : <Navigate to="/" replace />} />
          <Route path="projects/:id/changes/:n" element={me.is_staff ? <ChangePage /> : <Navigate to="/" replace />} />
          <Route path="projects/:id/questions" element={me.is_staff ? <QuestionsPage /> : <Navigate to="/" replace />} />
          <Route path="clients" element={me.is_staff ? <ClientList /> : <Navigate to="/" replace />} />
          <Route path="tasks" element={me.is_staff ? <MyTasks /> : <Navigate to="/" replace />} />
          <Route path="pipeline" element={me.is_staff ? <PipelinePage /> : <Navigate to="/" replace />} />
          <Route path="reports" element={me.is_staff ? <ReportsPage /> : <Navigate to="/" replace />} />
          <Route path="kits/:id" element={me.is_staff ? <KitPage /> : <Navigate to="/" replace />} />
          <Route path="clients/:id" element={me.is_staff ? <ClientDetail /> : <Navigate to="/" replace />} />
          <Route path="admin/users" element={<People />} />
          <Route path="admin/audit" element={<AuditLog />} />
          <Route path="admin/backups" element={me.is_staff ? <BackupsPage /> : <Navigate to="/" replace />} />
          <Route path="account" element={<Account />} />
          <Route path="*" element={<Page title="Not found"><p className="muted">That page doesn't exist.</p></Page>} />
        </Route>
      </Routes>
    </LiveProvider>
  );
}

// Review links (/review#<token>) are for people without an account: kept apart from the signed-in app.
function Root() {
  const location = useLocation();
  if (location.pathname === '/review') return <ReviewApp />;
  return <AuthProvider><App /></AuthProvider>;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrowserRouter>
          <Root />
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
